import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, FileText, ArrowUpRight, ArrowRight } from 'lucide-react';
import { products } from '../data/mockData';

const Products = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="products-page">
      {/* Hero (teal) */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-[#008080] via-[#0a7878] to-[#005a5a] text-white">
        <div className="absolute inset-0 bp-grid-on-dark opacity-50"></div>
        <div className="spotlight-gold bottom-0 right-0"></div>
        <span className="serial-ghost serial-ghost-light absolute right-4 top-12 text-[12rem] hidden md:block">RANGE</span>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="ark-overline ark-overline-light">// catalog · ark-2026</span>
          <h1 className="font-display font-black uppercase tracking-tighter text-white mt-5 text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.88]">
            Complete scaffolding<br />
            <span className="text-[#DAA520]">range on rent.</span>
          </h1>
          <p className="text-white/85 mt-7 max-w-2xl text-base md:text-lg leading-relaxed">
            Heavy-duty, safety-inspected scaffolding materials available on hire across Pune, PCMC and Maharashtra. Click any product for an instant quote.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href="tel:+919766601155" className="ark-btn-primary"><Phone size={16} /> Call Now</a>
            <Link to="/contact?source=hero_cta" className="ark-btn-secondary ark-btn-secondary-dark"><FileText size={16} /> Request Quote</Link>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="relative py-20 md:py-24 bg-gray-50">
        <div className="absolute inset-0 bp-grid-fine opacity-40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {products.map((p) => (
              <article
                key={p.id}
                className="ark-card-beam group relative bg-white flex flex-col"
                data-testid={`product-card-${p.id}`}
              >
                {/* Image module */}
                <div className="relative aspect-[4/3] bg-white overflow-hidden">
                  <div className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-white/95 backdrop-blur border border-[#008080]/40 px-2.5 py-1 rounded">
                    <span className="radar-dot"></span>
                    <span className="font-mono text-[10px] tracking-widest uppercase text-[#008080]">On Rent</span>
                  </div>
                  <div className="absolute top-4 right-4 z-20 font-mono text-[10px] tracking-[0.2em] text-gray-500 bg-white/95 px-2 py-1 uppercase border border-gray-200 rounded">
                    SKU · ARK-{String(p.id).padStart(3, '0')}
                  </div>
                  <div className="absolute bottom-4 left-4 z-20 bg-white p-1 rounded-sm shadow">
                    <img
                      src="https://customer-assets.emergentagent.com/job_8a6bf79d-629c-4621-9de5-4520ae741e2c/artifacts/15sezi0u_f665ed4a-3e98-483b-93e6-39a122c5327d.jpeg"
                      alt="ARK"
                      className="w-7 h-7 object-contain"
                    />
                  </div>
                  <img
                    src={p.image}
                    alt={`${p.name} scaffolding on rent in Pune & PCMC`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain p-8 group-hover:scale-110 transition-transform duration-700"
                  />
                </div>

                {/* Body */}
                <div className="flex-1 p-7 flex flex-col border-t border-gray-200">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="font-display font-bold text-[#008080] text-2xl md:text-3xl leading-tight">{p.name}</h2>
                    <ArrowUpRight className="text-gray-400 group-hover:text-[#DAA520] transition" size={20} />
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mt-3">{p.description}</p>

                  <div className="mt-5 p-4 border border-gray-200 bg-gray-50 rounded">
                    <p className="text-xs text-gray-600 leading-relaxed">{p.detailedDescription}</p>
                  </div>

                  {p.specifications && (
                    <div className="mt-5">
                      <h3 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#DAA520] mb-3">// Key Spec</h3>
                      <ul className="space-y-1.5">
                        {p.specifications.map((spec, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-[#008080] mt-0.5 font-bold">→</span>
                            <span>{spec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-6 flex gap-2">
                    <a
                      href={`https://wa.me/919766601155?text=${encodeURIComponent('Hi ARK Scaffolding, I need a rental quote for ' + p.name + '.')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-[#10B981] text-[#10B981] py-2.5 text-xs font-mono uppercase tracking-widest hover:bg-[#10B981] hover:text-white transition rounded"
                      data-testid={`product-whatsapp-${p.id}`}
                    >
                      <MessageCircle size={14} /> WhatsApp
                    </a>
                    <Link
                      to={`/contact?product=${encodeURIComponent(p.name)}&source=product_card`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#DAA520] text-black py-2.5 text-xs font-mono uppercase tracking-widest font-bold hover:bg-[#B8860B] hover:text-white transition rounded"
                      data-testid={`product-quote-${p.id}`}
                    >
                      <FileText size={14} /> Get Quote
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA (teal) */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-[#008080] to-[#005a5a] text-white">
        <div className="absolute inset-0 bp-grid-on-dark opacity-40"></div>
        <div className="spotlight-gold left-1/2 -translate-x-1/2 -top-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="ark-overline ark-overline-light justify-center">// Get on site</span>
          <h2 className="font-display font-black uppercase tracking-tighter text-white mt-5 text-4xl md:text-6xl leading-[0.9]">
            Need a quote for<br /><span className="text-[#DAA520]">your project?</span>
          </h2>
          <p className="text-white/85 mt-6 max-w-xl mx-auto">Talk to ARK Scaffolding for the best rental rates and fast delivery.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+919766601155" className="ark-btn-primary" data-testid="products-cta-call"><Phone size={16} /> Call Now</a>
            <Link to="/contact?source=hero_cta" className="ark-btn-secondary ark-btn-secondary-dark" data-testid="products-cta-quote">Request Quote <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
